/**
 * ShareForCreditsCard — V140 P0 Part 2.
 *
 * One-shot viral loop: trial workspace shares the agent on any social
 * channel and unlocks +20 actions on the chosen agent — exactly once
 * per workspace, per the product owner's hard constraint ("iba jedno
 * zdieľanie, nie opakované").
 *
 * Two visual states:
 *   1. Pre-redemption: agent picker + 4 share buttons. Clicking ANY
 *      share button calls /api/share/agent/{agent_id}/redeem so the
 *      bonus locks in atomically when they actually click out.
 *   2. Post-redemption: green confirmation chip showing which agent
 *      got the bonus and when.
 *
 * No spammy daily reminders — V140 explicitly killed repeated share
 * mechanics. One share, one reward, then we get out of the user's way.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../../config";
import { Share2, CheckCircle2, Sparkles, Facebook, Linkedin, MessageCircle } from "lucide-react";

const AGENTS = [
  { id: "helpdesk",            label: "Helpdesk Agent" },
  { id: "review-reply",        label: "Review Reply Agent" },
  { id: "cart-recovery",       label: "Cart Recovery Agent" },
  { id: "product-desc",        label: "Product Descriptions" },
  { id: "newsletter",          label: "Newsletter Agent" },
  { id: "social-media",        label: "Social Media Agent" },
  { id: "pricing-intel",       label: "Pricing Intel" },
  { id: "customer-onboarding", label: "Customer Onboarding" },
  { id: "review-moderator",    label: "Review Moderator" },
  { id: "behavior-analytics",  label: "Behavior Analytics" },
  { id: "gdpr-watch",          label: "GDPR Watch" },
];

const CHANNELS = [
  { key: "facebook", label: "Facebook", Icon: Facebook, color: "#1877F2" },
  { key: "x",        label: "X / Twitter", Icon: null,  color: "#000" },
  { key: "linkedin", label: "LinkedIn", Icon: Linkedin, color: "#0A66C2" },
  { key: "whatsapp", label: "WhatsApp", Icon: MessageCircle, color: "#25D366" },
];

export default function ShareForCreditsCard({ workspaceId, accent = "#FFB800" }) {
  const [status, setStatus] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState("helpdesk");
  const [share, setShare] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/share/agent/status/${workspaceId}`);
        if (!cancelled) setStatus(r.data);
      } catch (e) { /* silent — bar stays in default state */ }
    })();
    return () => { cancelled = true; };
  }, [workspaceId]);

  async function ensureShare() {
    if (share && share.agent_id === selectedAgent) return share;
    setBusy(true);
    setError("");
    try {
      const r = await axios.post(`${API}/share/agent/${selectedAgent}`, {
        workspace_id: workspaceId,
        origin: typeof window !== "undefined" ? window.location.origin : undefined,
      });
      setShare(r.data);
      return r.data;
    } catch (e) {
      setError(e?.response?.data?.detail || "share_create_failed");
      return null;
    } finally {
      setBusy(false);
    }
  }

  async function handleChannelClick(channel) {
    const s = await ensureShare();
    if (!s) return;
    // Open the share dialog FIRST (so popup-blockers don't trip on the
    // post-redeem await) — then redeem the one-time bonus.
    const popup = window.open(s.deep_links[channel], "_blank", "noopener,width=600,height=600");
    try {
      const r = await axios.post(`${API}/share/agent/${s.share_id}/redeem`);
      setStatus({ ...(status || {}), redeemed: true, redemption: r.data });
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      if (code === "already_redeemed") {
        setStatus({ ...(status || {}), redeemed: true });
      } else {
        setError("redeem_failed");
      }
    }
    if (popup) popup.focus();
  }

  if (!status) return null;

  // ─── State 2: already redeemed ──────────────────────────────────────────
  if (status.redeemed) {
    return (
      <section
        data-testid="share-for-credits-redeemed"
        className="mb-6 border border-emerald-500/40 bg-emerald-950/20 p-4 sm:p-5 flex items-start gap-3"
      >
        <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] font-black mb-1 text-emerald-300">
            Bonus aktivovaný
          </p>
          <p className="text-sm text-gray-200">
            +20 akcií si pridal pre <span className="font-black text-emerald-300">{status.redemption?.agent_id || "tvojho agenta"}</span>. Ďakujeme za zdieľanie!
          </p>
          <p className="text-[11px] text-gray-500 mt-1.5">
            Jeden bonus na workspace — zameraj sa teraz na reálne registrácie cez tvoj affiliate link (€29.70/mes provízia za každý paid signup).
          </p>
        </div>
      </section>
    );
  }

  // ─── State 1: pre-redemption ────────────────────────────────────────────
  return (
    <section
      data-testid="share-for-credits-card"
      className="mb-6 border border-gray-800 bg-neutral-950 p-4 sm:p-5"
    >
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] font-black mb-1" style={{ color: accent }}>
            <Sparkles className="w-3 h-3 inline mr-1.5" />
            Bonus +20 akcií
          </p>
          <p className="text-sm text-gray-200">
            Zdieľaj jedného agenta na sociálnej sieti a získaj <span className="font-black text-emerald-300">+20 akcií</span> navyše.
          </p>
          <p className="text-[11px] text-gray-500 mt-1">
            Jednorazový bonus na workspace — vyber agenta, klikni kanál, +20 akcií ti pribudne okamžite.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
        <div className="sm:col-span-1">
          <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1.5">
            Vyber agenta
          </label>
          <select
            data-testid="share-agent-select"
            value={selectedAgent}
            onChange={(e) => { setSelectedAgent(e.target.value); setShare(null); }}
            className="w-full bg-black border border-gray-800 text-gray-100 px-3 py-2 text-sm focus:outline-none focus:border-amber-500"
          >
            {AGENTS.map((a) => (
              <option key={a.id} value={a.id}>{a.label}</option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-2">
          <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1.5">
            Klikni kanál
          </p>
          <div className="flex flex-wrap gap-2">
            {CHANNELS.map(({ key, label, Icon, color }) => (
              <button
                key={key}
                data-testid={`share-channel-${key}`}
                onClick={() => handleChannelClick(key)}
                disabled={busy}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-white font-bold text-[12px] disabled:opacity-50 hover:opacity-90 transition-opacity"
                style={{ backgroundColor: color }}
              >
                {Icon ? <Icon className="w-3.5 h-3.5" /> : <Share2 className="w-3.5 h-3.5" />}
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <p className="mt-3 text-xs text-red-400" data-testid="share-error">
          {error}. Skús to ešte raz.
        </p>
      )}
    </section>
  );
}
